import { DetailedCatalystInsight } from '@api';
import { Icon, SkeletonLoader } from '@design';
import { getEntityTagFromSnake, getTickersFromSnakes } from '@helpers';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { InsightTypePill } from '@terminal/components/insight-type-pill/InsightTypePill';
import { InsightsLivePrice } from '@terminal/components/insights-live-price/InsightsLivePrice';
import { usePriceMarketReactionData } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { CatalystMarketReactionChart } from '@terminal/modules/market-reaction-charts/catalyst-market-reaction-chart/CatalystMarketReactionChart';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { queryKeys } from '@terminal/routes/query-params';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { EntityHeaderWatchlist } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { MarketCatalystReaction } from '../market-catalyst-reaction/MarketCatalystReaction';
import styles from './CatalystDrawerHeader.module.scss';

export interface CatalystDrawerHeaderProps {
  identifier: string;
  catalystInsight: DetailedCatalystInsight;
  isCompanyCatalyst?: boolean;
}

type MarketWideConfig = {
  iconName: React.ComponentProps<typeof Icon>['iconName'];
  fillColor: string;
};

const getMarketWideCategoryConfig = (
  subCategory: DetailedCatalystInsight['sub_category']
): MarketWideConfig | null => {
  if (subCategory === 'geopolitical_global_events') {
    return {
      iconName: 'Geopolitical',
      fillColor: 'var(--icon-decorative-purple)',
    };
  }

  if (subCategory === 'macroeconomic_policy_changes') {
    return {
      iconName: 'Macroeconomics',
      fillColor: 'var(--text-decorative-teal)',
    };
  }

  return null;
};

export const CatalystDrawerHeader = ({
  identifier,
  catalystInsight,
  isCompanyCatalyst,
}: CatalystDrawerHeaderProps) => {
  const { t } = useTranslation('insights');
  const companyEntityTag = isCompanyCatalyst ? identifier : undefined;
  const { params } = useQueryParams();
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  const themeId = !isCompanyCatalyst ? identifier : undefined;
  const entityTag = companyEntityTag
    ? companyEntityTag
    : getEntityTagFromSnake(catalystInsight.snakes[0]);
  const { data: entityData } = useEntity(entityTag);

  const { data: themeData } = useTheme({
    themeId,
  });
  const entity = entityData?.data;
  const isMultipleEntitiesChart = catalystInsight.snakes.length > 1;

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag,
    ticker: entity?.subscribable_ticker ?? '',
    reportingDate: catalystInsight.created_at,
    enabled: isCompanyCatalyst,
    isMultipleEntitiesChart,
    variant: 'catalyst',
  });

  /* i18next-extract-mark-context-next-line ["financial_operational_updates", "corporate_actions_strategic_announcements", "management_leadership_changes", "legal_regulatory_events"] */
  const translatedSubCategory = t(
    `catalyst.subcategories.${catalystInsight.sub_category}`
  );

  const getThemeHeader = () => {
    if (isCompanyCatalyst && entity) {
      return (
        <div className={styles.entityAndPriceWrapper}>
          <EntityHeaderWatchlist entity={entity} logoSize={48} />
          <InsightsLivePrice
            entity={entity}
            showPricePercentage
            waitForLiveTick
          />
        </div>
      );
    }

    if (themeData) {
      return (
        <div className={styles.iconWrapper}>
          <ThemeLogoIcon logo_url={themeData.logo_url} name={themeData.name} />
          <p className={styles.categoryTitle}>{themeData.name}</p>
        </div>
      );
    }

    const marketWideCategory = getMarketWideCategoryConfig(
      catalystInsight.sub_category
    );

    if (marketWideCategory) {
      return (
        <div className={styles.iconWrapper}>
          <div className={styles.marketWideIconWrapper}>
            <Icon
              iconName={marketWideCategory.iconName}
              size={48}
              fillColor={marketWideCategory.fillColor}
            />
          </div>
          <p className={styles.categoryTitle}>{translatedSubCategory}</p>
        </div>
      );
    }

    return null;
  };

  const getLeftHeaderNode = () => {
    if (!isMultipleEntitiesChart && entity) {
      return (
        <div className={styles.watchlistRow}>
          <EntityHeaderWatchlist entity={entity} />
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.root}>
      {!isExpanded && getThemeHeader()}
      <InsightTypePill
        createdAt={catalystInsight.created_at}
        subCategory={catalystInsight.sub_category}
        category={catalystInsight.category}
      />
      <div>
        <div className={styles.subCategoryActionTypeWrapper}>
          {themeId ||
          catalystInsight.sub_category === 'geopolitical_global_events' ? (
            <div className={styles.titleWrapper}>
              <span>{`${catalystInsight.action_type}`}</span>
            </div>
          ) : (
            `${translatedSubCategory} · ${catalystInsight.action_type}`
          )}
        </div>
        <div className={styles.title}>{catalystInsight.title}</div>
      </div>
      <div className={styles.titleGrid}>
        <div className={styles.chartRow}>
          <ErrorBoundaryWrapper>
            {isPriceMarketReactionDataLoading ? (
              <div className={styles.styledSkeletonLoaderWrapper}>
                <SkeletonLoader
                  areas={[
                    {
                      styles: {
                        width: '100%',
                        height: '400px',
                        borderRadius: '8px',
                      },
                    },
                  ]}
                />
              </div>
            ) : isCompanyCatalyst ? (
              <CatalystMarketReactionChart
                fromDateEST={fromDateEST}
                toDateEST={toDateEST}
                lines={
                  priceData
                    ? [
                        {
                          data: priceData,
                          prevCloseData,
                          lastProportionChange,
                          lastKnownPrice,
                          lastKnownDate,
                        },
                      ]
                    : []
                }
                entities={entity ? [entity] : undefined}
                leftHeaderNode={getLeftHeaderNode()}
              />
            ) : (
              <MarketCatalystReaction
                createdAt={catalystInsight.created_at}
                tags={catalystInsight.snakes.map(getEntityTagFromSnake)}
                tickers={getTickersFromSnakes(catalystInsight.snakes)}
                leftHeaderNode={getLeftHeaderNode()}
              />
            )}
          </ErrorBoundaryWrapper>
        </div>
      </div>
    </div>
  );
};
